/**
 * Invite Key Service
 *
 * Manages invite keys for organization signup with trial periods.
 * Admin generates keys, users enter them during signup to get
 * trial access to the Individual plan.
 */

import { supabase } from './supabaseService';

// =============================================================================
// TYPES
// =============================================================================

export interface InviteKey {
  id: string;
  code: string;
  planId: string;
  trialDays: number;
  maxUses: number;
  currentUses: number;
  isActive: boolean;
  expiresAt: string | null;
  createdBy: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InviteKeyUsage {
  id: string;
  inviteKeyId: string;
  organizationId: string;
  usedAt: string;
}

export interface CreateInviteKeyOptions {
  planId?: string;
  trialDays?: number;
  maxUses?: number;
  expiresAt?: string;
  notes?: string;
  createdBy?: string;
}

export interface ValidateInviteKeyResult {
  valid: boolean;
  key?: InviteKey;
  error?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const CODE_PREFIX = 'PSICO-';
const CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const CODE_SUFFIX_LENGTH = 6;
const MAX_CODE_GENERATION_ATTEMPTS = 10;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate a unique invite code
 * Format: PSICO-XXXXXX (where X is alphanumeric)
 */
const generateInviteCode = (): string => {
  let suffix = '';
  for (let i = 0; i < CODE_SUFFIX_LENGTH; i++) {
    suffix += CODE_CHARS.charAt(Math.floor(Math.random() * CODE_CHARS.length));
  }
  return `${CODE_PREFIX}${suffix}`;
};

/**
 * Map database row to InviteKey interface
 */
const mapDbToInviteKey = (data: any): InviteKey => ({
  id: data.id,
  code: data.code,
  planId: data.plan_id,
  trialDays: data.trial_days,
  maxUses: data.max_uses,
  currentUses: data.current_uses,
  isActive: data.is_active,
  expiresAt: data.expires_at,
  createdBy: data.created_by,
  notes: data.notes,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
});

// =============================================================================
// ADMIN FUNCTIONS
// =============================================================================

/**
 * Create a new invite key
 * @param options - Configuration options for the key
 * @returns The created invite key or null if failed
 */
export const createInviteKey = async (
  options: CreateInviteKeyOptions = {}
): Promise<InviteKey | null> => {
  try {
    let code = generateInviteCode();
    let attempts = 0;

    // Ensure code is unique
    while (attempts < MAX_CODE_GENERATION_ATTEMPTS) {
      const { data: exists } = await supabase
        .from('invite_keys')
        .select('code')
        .eq('code', code)
        .single();

      if (!exists) break;
      code = generateInviteCode();
      attempts++;
    }

    if (attempts >= MAX_CODE_GENERATION_ATTEMPTS) {
      console.error('Failed to generate unique invite code');
      return null;
    }

    const { data, error } = await supabase
      .from('invite_keys')
      .insert({
        code,
        plan_id: options.planId || 'individual',
        trial_days: options.trialDays ?? 30,
        max_uses: options.maxUses ?? 1,
        expires_at: options.expiresAt || null,
        notes: options.notes || null,
        created_by: options.createdBy || null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating invite key:', error);
      return null;
    }

    return mapDbToInviteKey(data);
  } catch (error) {
    console.error('Error in createInviteKey:', error);
    return null;
  }
};

/**
 * List all invite keys (for admin dashboard)
 * @returns Array of invite keys
 */
export const listInviteKeys = async (): Promise<InviteKey[]> => {
  try {
    const { data, error } = await supabase
      .from('invite_keys')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error listing invite keys:', error);
      return [];
    }

    return (data || []).map(mapDbToInviteKey);
  } catch (error) {
    console.error('Error in listInviteKeys:', error);
    return [];
  }
};

/**
 * Get a single invite key by ID
 * @param keyId - The invite key ID
 * @returns The invite key or null
 */
export const getInviteKey = async (keyId: string): Promise<InviteKey | null> => {
  try {
    const { data, error } = await supabase
      .from('invite_keys')
      .select('*')
      .eq('id', keyId)
      .single();

    if (error || !data) {
      return null;
    }

    return mapDbToInviteKey(data);
  } catch (error) {
    console.error('Error in getInviteKey:', error);
    return null;
  }
};

/**
 * Deactivate an invite key
 * @param keyId - The invite key ID to deactivate
 * @returns True if successful
 */
export const deactivateInviteKey = async (keyId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('invite_keys')
      .update({
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', keyId);

    if (error) {
      console.error('Error deactivating invite key:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deactivateInviteKey:', error);
    return false;
  }
};

/**
 * Reactivate an invite key
 * @param keyId - The invite key ID to reactivate
 * @returns True if successful
 */
export const reactivateInviteKey = async (keyId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('invite_keys')
      .update({
        is_active: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', keyId);

    if (error) {
      console.error('Error reactivating invite key:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in reactivateInviteKey:', error);
    return false;
  }
};

/**
 * Delete an invite key permanently
 * @param keyId - The invite key ID to delete
 * @returns True if successful
 */
export const deleteInviteKey = async (keyId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('invite_keys')
      .delete()
      .eq('id', keyId);

    if (error) {
      console.error('Error deleting invite key:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteInviteKey:', error);
    return false;
  }
};

/**
 * Get usage history for an invite key
 * @param keyId - The invite key ID
 * @returns Array of usage records
 */
export const getInviteKeyUsage = async (keyId: string): Promise<InviteKeyUsage[]> => {
  try {
    const { data, error } = await supabase
      .from('invite_key_usage')
      .select('*')
      .eq('invite_key_id', keyId)
      .order('used_at', { ascending: false });

    if (error) {
      console.error('Error fetching invite key usage:', error);
      return [];
    }

    return (data || []).map(row => ({
      id: row.id,
      inviteKeyId: row.invite_key_id,
      organizationId: row.organization_id,
      usedAt: row.used_at,
    }));
  } catch (error) {
    console.error('Error in getInviteKeyUsage:', error);
    return [];
  }
};

// =============================================================================
// PUBLIC FUNCTIONS (for signup flow)
// =============================================================================

/**
 * Validate an invite key code
 * @param code - The invite code to validate
 * @returns Validation result with key data if valid
 */
export const validateInviteKey = async (
  code: string
): Promise<ValidateInviteKeyResult> => {
  try {
    // Normalize code to uppercase
    const normalizedCode = code.trim().toUpperCase();

    const { data, error } = await supabase
      .from('invite_keys')
      .select('*')
      .eq('code', normalizedCode)
      .eq('is_active', true)
      .single();

    if (error || !data) {
      return {
        valid: false,
        error: 'Chave de convite inválida ou não encontrada'
      };
    }

    const key = mapDbToInviteKey(data);

    // Check expiration
    if (key.expiresAt && new Date(key.expiresAt) < new Date()) {
      return {
        valid: false,
        error: 'Chave de convite expirada'
      };
    }

    // Check usage limit
    if (key.currentUses >= key.maxUses) {
      return {
        valid: false,
        error: 'Chave de convite ja atingiu o limite de uso'
      };
    }

    return { valid: true, key };
  } catch (error) {
    console.error('Error in validateInviteKey:', error);
    return {
      valid: false,
      error: 'Erro ao validar chave de convite'
    };
  }
};

/**
 * Mark an invite key as used
 * Records the usage and increments the counter
 * @param keyId - The invite key ID
 * @param organizationId - The organization that used the key
 * @returns True if successful
 */
export const markInviteKeyUsed = async (
  keyId: string,
  organizationId: string
): Promise<boolean> => {
  try {
    // Record usage
    const { error: usageError } = await supabase
      .from('invite_key_usage')
      .insert({
        invite_key_id: keyId,
        organization_id: organizationId,
      });

    if (usageError) {
      console.error('Error recording key usage:', usageError);
      return false;
    }

    // Get current usage count
    const { data: keyData, error: fetchError } = await supabase
      .from('invite_keys')
      .select('current_uses')
      .eq('id', keyId)
      .single();

    if (fetchError || !keyData) {
      console.error('Error fetching key for update:', fetchError);
      return false;
    }

    // Increment usage counter
    const { error: updateError } = await supabase
      .from('invite_keys')
      .update({
        current_uses: keyData.current_uses + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', keyId);

    if (updateError) {
      console.error('Error updating key usage count:', updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in markInviteKeyUsed:', error);
    return false;
  }
};

// =============================================================================
// STATISTICS
// =============================================================================

/**
 * Get statistics about invite keys
 * @returns Statistics object
 */
export const getInviteKeyStats = async (): Promise<{
  totalKeys: number;
  activeKeys: number;
  totalUses: number;
  keysUsedToday: number;
}> => {
  try {
    // Get all keys
    const { data: keys } = await supabase
      .from('invite_keys')
      .select('is_active, current_uses');

    const totalKeys = keys?.length || 0;
    const activeKeys = keys?.filter(k => k.is_active).length || 0;
    const totalUses = keys?.reduce((sum, k) => sum + (k.current_uses || 0), 0) || 0;

    // Get today's usage
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { count } = await supabase
      .from('invite_key_usage')
      .select('*', { count: 'exact', head: true })
      .gte('used_at', today.toISOString());

    return {
      totalKeys,
      activeKeys,
      totalUses,
      keysUsedToday: count || 0,
    };
  } catch (error) {
    console.error('Error in getInviteKeyStats:', error);
    return {
      totalKeys: 0,
      activeKeys: 0,
      totalUses: 0,
      keysUsedToday: 0,
    };
  }
};

export default {
  createInviteKey,
  listInviteKeys,
  getInviteKey,
  deactivateInviteKey,
  reactivateInviteKey,
  deleteInviteKey,
  getInviteKeyUsage,
  validateInviteKey,
  markInviteKeyUsed,
  getInviteKeyStats,
};
